import React, { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const emptyUser = { username:'', password:'', full_name:'', email:'', campus_ids:[], permission_role_id:'', department:'', departments:[] };

// ─── Bulk Update Modal ────────────────────────────────────────────────────────
function BulkUserModal({ count, campuses, roles, onSave, onClose, busy }) {
  const [fields, setFields] = useState({ active: false, permission_role_id: false, campus_ids: false });
  const [vals,   setVals]   = useState({ active: true, permission_role_id:'', campus_ids:[] });
  const [err,    setErr]    = useState('');

  const toggle = key => setFields(f => ({ ...f, [key]: !f[key] }));
  const toggleCampus = id => setVals(v => ({ ...v, campus_ids: v.campus_ids.includes(id) ? v.campus_ids.filter(c => c !== id) : [...v.campus_ids, id] }));

  const handleSave = () => {
    const updates = {};
    if (fields.active)             updates.active             = vals.active;
    if (fields.permission_role_id && vals.permission_role_id) updates.permission_role_id = vals.permission_role_id;
    if (fields.campus_ids)         updates.campus_ids         = vals.campus_ids;
    if (!Object.keys(updates).length) { setErr('Select at least one field to update'); return; }
    onSave(updates);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:520 }}>
        <div className="modal-header">
          <span className="modal-title">Bulk Update — {count} User{count > 1 ? 's' : ''}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <p style={{ fontSize:13, color:'var(--text2)', marginBottom:16 }}>Tick the fields you want to update. Unticked fields will not be changed.</p>

        {/* Status */}
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
          <input type="checkbox" checked={fields.active} onChange={() => toggle('active')} style={{ width:16, height:16 }} />
          <div className="form-group" style={{ flex:1, marginBottom:0, opacity: fields.active ? 1 : 0.4 }}>
            <label>Status</label>
            <select disabled={!fields.active} value={vals.active ? 'active' : 'inactive'} onChange={e => setVals(v => ({ ...v, active: e.target.value === 'active' }))}>
              <option value="active">Active</option><option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Permission Role */}
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
          <input type="checkbox" checked={fields.permission_role_id} onChange={() => toggle('permission_role_id')} style={{ width:16, height:16 }} />
          <div className="form-group" style={{ flex:1, marginBottom:0, opacity: fields.permission_role_id ? 1 : 0.4 }}>
            <label>Role</label>
            <select disabled={!fields.permission_role_id} value={vals.permission_role_id} onChange={e => setVals(v => ({ ...v, permission_role_id: e.target.value }))}>
              <option value="">— Select —</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
        </div>

        {/* Campus assignment */}
        <div style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:12 }}>
          <input type="checkbox" checked={fields.campus_ids} onChange={() => toggle('campus_ids')} style={{ width:16, height:16, marginTop:4 }} />
          <div className="form-group" style={{ flex:1, marginBottom:0, opacity: fields.campus_ids ? 1 : 0.4 }}>
            <label>Replace Campus Assignment</label>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:4, maxHeight:150, overflowY:'auto', padding:8, background:'var(--bg3)', borderRadius:'var(--radius)', border:'1px solid var(--border)' }}>
              {campuses.map(c => (
                <label className="opt" key={c.id} style={{ display:'flex', alignItems:'center', gap:6, cursor: fields.campus_ids ? 'pointer' : 'default', fontSize:12, color:'var(--text)', padding:'3px 4px', borderRadius:3, background: vals.campus_ids.includes(c.id) ? 'rgba(79,142,247,0.1)' : 'transparent' }}>
                  <input type="checkbox" disabled={!fields.campus_ids} checked={vals.campus_ids.includes(c.id)} onChange={() => toggleCampus(c.id)} style={{ width:13, height:13 }} />
                  {c.name}
                </label>
              ))}
            </div>
          </div>
        </div>

        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={busy}>{busy ? 'Updating…' : `Update ${count} User${count > 1 ? 's' : ''}`}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Users Page ───────────────────────────────────────────────────────────────
export default function Users() {
  const { isAdmin } = useAuth();
  const [users,     setUsers]     = useState([]);
  const [noLogin,   setNoLogin]   = useState([]);   // directory staff with no portal login
  const [loginBusy, setLoginBusy] = useState(null);
  const [campuses,  setCampuses]  = useState([]);
  const [roles,     setRoles]     = useState([]);
  const [staff,     setStaff]     = useState([]); // all staff across campuses for "import" feature
  const [modal,     setModal]     = useState(false);
  const [handover,  setHandover]  = useState(null);   // the person who has left
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState(emptyUser);
  const [busy,      setBusy]      = useState(false);
  const [err,       setErr]       = useState('');
  const [selected,  setSelected]  = useState(new Set());
  const [bulkModal, setBulkModal] = useState(false);
  const [departments, setDepartments] = useState([]);

  // "Import from Staff" — selected staff for pre-fill
  const [staffSearch,   setStaffSearch]   = useState('');
  const [showStaffDrop, setShowStaffDrop] = useState(false);

  const load = async () => {
    try {
      const [u, c, r, d] = await Promise.all([api.get('/master/users'), api.get('/master/campuses'), api.get('/master/roles'), api.get('/master/departments')]);
      setUsers(u.data); setCampuses(c.data); setRoles(r.data.filter(r => r.active)); setDepartments(d.data);
    } catch(e) {}
    try { const n = await api.get('/master/staff-no-login'); setNoLogin(n.data); } catch(e) {}
    setSelected(new Set());
  };

  // Give a directory staff member portal access (sends the welcome email)
  const createLogin = async s => {
    if (!window.confirm(`Create a login for ${s.name} (${s.email})?\n\nThey'll receive the welcome email and can sign in with this address.`)) return;
    setLoginBusy(s.id);
    try { await api.post(`/master/staff/${s.id}/create-login`); load(); }
    catch (e) { window.alert(e.response?.data?.error || 'Could not create login'); }
    finally { setLoginBusy(null); }
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  // Load all staff for import (all active campuses)
  useEffect(() => {
    if (isAdmin && campuses.length) {
      Promise.all(campuses.filter(c => c.active).map(c => api.get(`/master/informedby/${c.id}`)))
        .then(results => setStaff(results.flatMap(r => r.data.filter(s => s.active)).sort((a,b) => a.name.localeCompare(b.name))))
        .catch(() => {});
    }
  }, [campuses, isAdmin]);

  if (!isAdmin) return <div style={{ padding:40, color:'var(--text2)', textAlign:'center' }}>Admin access required</div>;

  // Someone who never did anything is deleted outright. Someone who worked here
  // cannot be — their tickets and log entries point at them — so they are
  // "removed" instead: personal details erased, the record of their work kept.
  const removeUser = async u => {
    const ok = window.confirm(
      `Remove ${u.full_name} from the organisation?\n\n` +
      `• Their name, login, email and campuses are erased\n` +
      `• They can never sign in again\n` +
      `• Work they did stays on record as "Former staff (${u.full_name})"\n\n` +
      `This cannot be undone.`);
    if (!ok) return;
    try {
      await api.post(`/master/users/${u.id}/remove`);
      window.alert(`${u.full_name} has been removed.`);
      load();
    } catch (e) {
      const d = e.response?.data;
      if (d?.needs_handover) {
        if (window.confirm(`${d.error}\n\nOpen Handover now?`)) setHandover(u);
      } else window.alert(d?.error || 'Could not remove the user');
    }
  };

  const deleteUser = async u => {
    if (!window.confirm(`Permanently delete user "${u.username}" (${u.full_name})?\n\nThis cannot be undone.`)) return;
    try { await api.delete(`/master/users/${u.id}`); load(); }
    catch(e) {
      const d = e.response?.data;
      if (d?.can_remove) {
        if (window.confirm(`${d.error}\n\nRemove them from the organisation instead? Their personal details are erased and the work they did stays on record.`))
          removeUser(u);
      } else window.alert(d?.error || 'Delete failed');
    }
  };

  // Ends their open sessions. Useful after changing someone's role or campuses:
  // a login token lasts 8 hours, so without this they keep the old access until
  // they happen to sign out themselves.
  const signOut = async u => {
    if (!window.confirm(`Sign ${u.full_name} out of the portal?\n\nAny device they are signed in on returns to the login screen. They can sign straight back in — this only refreshes their access.`)) return;
    try { await api.post(`/master/users/${u.id}/signout`); window.alert(`${u.full_name} has been signed out.`); }
    catch (e) { window.alert(e.response?.data?.error || 'Could not sign them out'); }
  };

  const openNew = () => { setForm(emptyUser); setEditing(null); setErr(''); setStaffSearch(''); setShowStaffDrop(false); setModal(true); };
  const openEdit = u => {
    setForm({ username: u.username, password:'', full_name: u.full_name, email: u.email||'',
      campus_ids: u.campus_ids || [], active: u.active, permission_role_id: u.permission_role_id || '',
      department: u.department || '', departments: u.departments || [] });
    setEditing(u); setErr(''); setStaffSearch(''); setModal(true);
  };

  // Import staff into new user form
  const importStaff = s => {
    const username = s.name.toLowerCase().replace(/\s+/g,'.').replace(/[^a-z0-9.]/g,'');
    setForm(f => ({ ...f, full_name: s.name, email: s.email || '', username }));
    setStaffSearch(s.name); setShowStaffDrop(false);
  };

  const toggleCampus = id => setForm(f => ({ ...f, campus_ids: f.campus_ids.includes(id) ? f.campus_ids.filter(c => c !== id) : [...f.campus_ids, id] }));

  const save = async () => {
    if (!form.full_name)                { setErr('Full name required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((form.email||'').trim())) { setErr('A valid email is required'); return; }
    if (!editing && !form.username)     { setErr('Username required'); return; }
    if (!form.permission_role_id)       { setErr('Select a role'); return; }
    if (!form.campus_ids.length)        { setErr('Select at least one campus'); return; }
    setBusy(true); setErr('');
    try {
      const isCoord = roles.find(r => String(r.id) === String(form.permission_role_id))?.base_type === 'coordinator';
      if (editing) {
        const payload = { full_name: form.full_name, email: form.email,
          campus_ids: form.campus_ids, active: form.active,
          permission_role_id: form.permission_role_id, department: form.department || null,
          departments: isCoord ? form.departments : [] };
        if (form.password) payload.password = form.password;
        await api.put(`/master/users/${editing.id}`, payload);
      } else {
        await api.post('/master/users', {
          username: form.username, password: form.password, full_name: form.full_name,
          email: form.email, campus_ids: form.campus_ids, permission_role_id: form.permission_role_id,
          department: form.department || null, departments: isCoord ? form.departments : []
        });
      }
      setModal(false); load();
    } catch(e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };

  // Bulk
  const allIds = users.map(u => u.id);
  const allSelected = allIds.length > 0 && allIds.every(id => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds));
  const toggleOne = id => { const next = new Set(selected); next.has(id) ? next.delete(id) : next.add(id); setSelected(next); };

  const doBulkUpdate = async updates => {
    setBusy(true);
    try { await api.post('/master/users/bulk-update', { ids: [...selected], updates }); setBulkModal(false); load(); } catch(e) {}
    finally { setBusy(false); }
  };

  const filteredStaff = staff.filter(s => !staffSearch || s.name.toLowerCase().includes(staffSearch.toLowerCase()));

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:20 }}>
        <h2 style={{ fontSize:18, fontWeight:600 }}>User Management</h2>
        <div className="flex-gap" style={{ gap:8 }}>
          {selected.size > 0 && (
            <button className="btn btn-secondary" onClick={() => setBulkModal(true)}>✎ Update {selected.size} Selected</button>
          )}
          <button className="btn btn-primary" onClick={openNew}>+ Add User</button>
        </div>
      </div>

      {/* Staff added from the ticket form who have no portal login yet */}
      {noLogin.length > 0 && (
        <div className="card" style={{ marginBottom:16, padding:0, overflow:'hidden' }}>
          <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)' }}>
            <div style={{ fontSize:14, fontWeight:600 }}>👥 Staff without a login ({noLogin.length})</div>
            <div style={{ fontSize:12, color:'var(--text2)', marginTop:2 }}>
              Added via the ticket form. Click <b>Create Login</b> to give them portal access — they get the welcome
              email and can then fill in their own details (department, subject, class teacher).
            </div>
          </div>
          <div className="table-wrap"><table>
            <thead><tr><th>Name</th><th>Email</th><th>Designation</th><th>Campus</th><th></th></tr></thead>
            <tbody>{noLogin.map(s => (
              <tr key={s.id}>
                <td style={{ fontWeight:500 }}>{s.name}</td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{s.email}</td>
                <td style={{ fontSize:12 }}>{s.designation || '—'}</td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{s.campus_name || '—'}</td>
                <td><button className="btn btn-primary btn-sm" disabled={loginBusy===s.id} onClick={() => createLogin(s)}>
                  {loginBusy===s.id ? 'Creating…' : '🔑 Create Login'}
                </button></td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>
      )}

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!users.length ? <div className="empty-state"><h3>No users found</h3></div> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width:36 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ width:14, height:14 }} /></th>
                  <th>Name</th><th>Username</th><th>Role</th><th>Campuses</th><th>Last Login</th><th>Status</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ background: selected.has(u.id) ? 'rgba(79,142,247,0.06)' : '' }}>
                    <td><input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleOne(u.id)} style={{ width:14, height:14 }} /></td>
                    <td>
                      <div style={{ fontWeight:500 }}>{u.full_name}</div>
                      {u.email && <div style={{ fontSize:12, color:'var(--text2)' }}>{u.email}</div>}
                    </td>
                    <td><span className="mono" style={{ fontSize:12 }}>{u.username}</span></td>
                    <td>
                      <span className={`badge ${u.role === 'admin' ? 'badge-high' : u.role === 'requester' ? 'badge-discuss' : u.role === 'principal' ? 'badge-discussed' : 'badge-normal'}`}>
                        {u.permission_role_name || (u.role === 'admin' ? 'Administrator' : u.role)}
                      </span>
                    </td>
                    <td><span style={{ fontSize:12, color:'var(--text2)' }}>{u.campus_names?.join(', ') || '—'}</span></td>
                    <td><span style={{ fontSize:12, color:'var(--text2)' }}>{u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}</span></td>
                    <td>
                      {u.role === 'requester' && !u.active
                        ? <span className="badge badge-pending">⏳ Pending</span>
                        : <span className={`badge ${u.active ? 'badge-completed' : 'badge-faulty'}`}>{u.active ? 'Active' : 'Inactive'}</span>}
                      {u.reset_requested && <div style={{ marginTop:4 }}><span className="badge badge-repair">🔑 Reset requested</span></div>}
                    </td>
                    <td>
                      <div className="flex-gap" style={{ gap:6 }}>
                        {u.role === 'requester' && !u.active && (
                          <button className="btn btn-primary btn-sm" onClick={async () => { await api.put(`/master/users/${u.id}`, { active:true }); load(); }}>✓ Approve</button>
                        )}
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(u)}>Edit</button>
                        {u.active && (
                          <button className="btn btn-secondary btn-sm" title="End their open sessions — they must sign in again"
                            onClick={() => signOut(u)}>Sign out</button>
                        )}
                        {/* For someone who has left: move their open work to a
                            colleague before the account goes quiet. */}
                        <button className="btn btn-secondary btn-sm" title="They have left — hand their work to someone else"
                          onClick={() => setHandover(u)}>Handover</button>
                        <button className="btn btn-danger btn-sm" onClick={() => deleteUser(u)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && false && setModal(false)}>
          <div className="modal" style={{ maxWidth:580 }}>
            <div className="modal-header">
              <span className="modal-title">{editing ? `Edit User — ${editing.username}` : 'Add New User'}</span>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>

            {/* Import from Staff — only on add */}
            {!editing && staff.length > 0 && (
              <div className="form-group" style={{ position:'relative', marginBottom:16 }}>
                <label style={{ color:'var(--accent)' }}>Import from Staff Details <span style={{ fontSize:11, color:'var(--text2)', fontWeight:400 }}>(pre-fills name, email, username)</span></label>
                <input
                  value={staffSearch}
                  onChange={e => { setStaffSearch(e.target.value); setShowStaffDrop(true); }}
                  onFocus={() => setShowStaffDrop(true)}
                  placeholder="Search staff name to pre-fill…"
                  autoComplete="off"
                />
                {showStaffDrop && filteredStaff.length > 0 && (
                  <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:99, background:'var(--bg2)', border:'1px solid var(--accent)', borderRadius:'0 0 var(--radius) var(--radius)', maxHeight:160, overflowY:'auto', boxShadow:'0 8px 24px rgba(0,0,0,0.4)' }}>
                    {filteredStaff.map(s => (
                      <div key={s.id} onClick={() => importStaff(s)}
                        style={{ padding:'8px 12px', cursor:'pointer', fontSize:13, borderBottom:'1px solid var(--border)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                        <div style={{ fontWeight:500 }}>{s.name}</div>
                        {s.designation && <div style={{ fontSize:11, color:'var(--text2)' }}>{s.designation}</div>}
                      </div>
                    ))}
                  </div>
                )}
                {staffSearch && filteredStaff.length === 0 && showStaffDrop && (
                  <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:99, background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:'0 0 var(--radius) var(--radius)', padding:'10px 12px', fontSize:12, color:'var(--text2)' }}>No matching staff found</div>
                )}
              </div>
            )}

            <div className="form-row">
              <div className="form-group"><label>Full Name *</label><input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} /></div>
              <div className="form-group"><label>Email *</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="name@tipsglobeducate.com" /></div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Username {editing ? <span style={{ fontSize:11, color:'var(--text2)' }}>(cannot change)</span> : '*'}</label>
                <input value={form.username} disabled={!!editing} onChange={e => setForm(f => ({ ...f, username: e.target.value.toLowerCase() }))} />
              </div>
              <div className="form-group">
                <label>{editing ? 'New Password (blank = keep)' : 'Password (optional)'}</label>
                <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder={editing ? 'Leave blank to keep current' : 'Blank = Google sign-in only'} />
              </div>
            </div>

            <div className="form-group">
              <label>Role *</label>
              <select value={form.permission_role_id} onChange={e => setForm(f => ({ ...f, permission_role_id: e.target.value }))}>
                <option value="">— Select a role —</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>Manage roles & their permissions under the <strong>Roles</strong> tab.</div>
            </div>

            {/* Staff department (used for coordinator visibility & dept reports) */}
            <div className="form-group">
              <label>Department</label>
              <select value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))}>
                <option value="">— None —</option>
                {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </div>

            {/* Coordinator: which department(s) they oversee */}
            {roles.find(r => String(r.id) === String(form.permission_role_id))?.base_type === 'coordinator' && (
              <div className="form-group">
                <label>Coordinator Departments *</label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {departments.map(d => {
                    const on = form.departments.includes(d.name);
                    return <button type="button" key={d.id} className={`btn btn-sm ${on?'btn-primary':'btn-secondary'}`}
                      onClick={() => setForm(f => ({ ...f, departments: on ? f.departments.filter(x=>x!==d.name) : [...f.departments, d.name] }))}>{d.name}</button>;
                  })}
                </div>
                <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>The coordinator will see all tickets raised by staff in these departments.</div>
              </div>
            )}

            {editing && (
              <div className="form-group">
                <label>Status</label>
                <select value={form.active ? 'active' : 'inactive'} onChange={e => setForm(f => ({ ...f, active: e.target.value === 'active' }))}>
                  <option value="active">Active</option><option value="inactive">Inactive</option>
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Assign Campuses *</label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, maxHeight:200, overflowY:'auto', padding:10, background:'var(--bg3)', borderRadius:'var(--radius)', border:'1px solid var(--border)' }}>
                {campuses.filter(c => c.active).map(c => (
                  <label className="opt" key={c.id} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12, textTransform:'none', letterSpacing:0, color:'var(--text)', padding:'4px 6px', borderRadius:4, background: form.campus_ids.includes(c.id) ? 'rgba(79,142,247,0.1)' : 'transparent' }}>
                    <input type="checkbox" checked={form.campus_ids.includes(c.id)} onChange={() => toggleCampus(c.id)} style={{ width:14, height:14, flexShrink:0 }} />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>

            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : editing ? 'Save Changes' : 'Create User'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Update Modal */}
      {bulkModal && (
        <BulkUserModal
          count={selected.size}
          campuses={campuses.filter(c => c.active)}
          roles={roles}
          onSave={doBulkUpdate}
          onClose={() => setBulkModal(false)}
          busy={busy}
        />
      )}

      {handover && (
        <HandoverModal user={handover} onClose={() => setHandover(null)}
          onDone={() => { setHandover(null); load(); }} />
      )}
    </div>
  );
}

// ── Handover ────────────────────────────────────────────────────────────────
// Someone has left. Their open work moves to a colleague and the account is
// switched off — history stays as it was, because rewriting who did past work
// would make the audit trail untrue. That is also why the account itself
// usually cannot be deleted.
function HandoverModal({ user, onClose, onDone }) {
  const [d, setD] = useState(null);
  const [to, setTo] = useState('');
  const [deactivate, setDeactivate] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get(`/master/users/${user.id}/handover`)
      .then(r => setD(r.data)).catch(() => setErr('Could not load their pending work'));
  }, [user.id]);

  const go = async () => {
    if (!to) return setErr('Pick who takes over');
    setBusy(true); setErr('');
    try {
      const r = await api.post(`/master/users/${user.id}/handover`,
        { to_user_id: Number(to), deactivate });
      window.alert(`${r.data.total} item(s) moved to ${r.data.to}.` +
        (deactivate ? `\n\n${user.full_name}'s account is now switched off.` : ''));
      onDone();
    } catch (e) { setErr(e.response?.data?.error || 'Handover failed'); }
    finally { setBusy(false); }
  };

  const sameCampus = (d?.candidates || []).filter(c => c.same_campus);
  const others     = (d?.candidates || []).filter(c => !c.same_campus);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:560 }}>
        <div className="modal-header">
          <span className="modal-title">Handover — {user.full_name}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding:16 }}>
          {err && <div style={{ color:'var(--red)', fontSize:12.5, marginBottom:10 }}>{err}</div>}
          {!d ? <div style={{ fontSize:13, color:'var(--text2)' }}>Checking what is still open…</div> : (
            <>
              <div style={{ fontSize:12.5, color:'var(--text2)', marginBottom:10 }}>
                {d.user.campus_names || 'No campus'} · {d.user.role.replace('_', ' ')}
              </div>

              {d.pending.length === 0 ? (
                <div className="card" style={{ marginBottom:14 }}>
                  <div style={{ fontSize:13 }}>Nothing is still assigned to them. You can switch the account off.</div>
                </div>
              ) : (
                <div className="card" style={{ marginBottom:14, padding:0, overflow:'hidden' }}>
                  <div className="table-wrap"><table>
                    <thead><tr><th>Still on them</th><th style={{ textAlign:'right', width:70 }}>Count</th></tr></thead>
                    <tbody>
                      {d.pending.map(p => (
                        <tr key={p.key}>
                          <td style={{ fontSize:12.5 }}>
                            {p.label}
                            {p.note && <div style={{ fontSize:11, color:'var(--text2)' }}>{p.note}</div>}
                          </td>
                          <td style={{ textAlign:'right', fontWeight:700 }}>{p.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                </div>
              )}

              <div className="form-group">
                <label>Who takes this over?</label>
                {/* Colleagues at the same campus come first — a handover almost
                    always goes to whoever covers the same site. */}
                <select value={to} onChange={e => setTo(e.target.value)}>
                  <option value="">— choose a person —</option>
                  {sameCampus.length > 0 && (
                    <optgroup label="Same campus">
                      {sameCampus.map(c => (
                        <option key={c.id} value={c.id}>{c.full_name} — {c.campus_names || 'no campus'}</option>
                      ))}
                    </optgroup>
                  )}
                  {others.length > 0 && (
                    <optgroup label="Other IT staff">
                      {others.map(c => (
                        <option key={c.id} value={c.id}>{c.full_name} — {c.campus_names || 'no campus'}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
                {sameCampus.length === 0 && (
                  <div style={{ fontSize:11.5, color:'#B26A00', marginTop:4 }}>
                    Nobody else covers {d.user.campus_names || 'their campus'} — whoever takes over
                    should be given that campus in their profile too.
                  </div>
                )}
              </div>

              <label className="opt" style={{ marginBottom:12 }}>
                <input type="checkbox" checked={deactivate} onChange={e => setDeactivate(e.target.checked)} />
                <span>Switch off {user.full_name}'s account and end their sessions</span>
              </label>

              <div className="flex-gap" style={{ gap:8 }}>
                <button className="btn btn-primary btn-sm" disabled={busy || !to} onClick={go}>
                  {busy ? 'Moving…' : `Hand over ${d.total || 0} item(s)`}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
